/**
 * Bản nháp ô "Dịp chụp" (QĐ 0480) và luật đổi sang thân gửi máy chủ — cùng luật
 * với occasion.normalize ở máy chủ, để nút Lưu khoá đúng chỗ máy chủ sẽ trả 400.
 */
import {
  BIRTHDAY_NO_YEAR,
  OCCASION_SOON_DAYS,
  daysUntilAnnual,
  findBirthdayMention,
  formatBirthday,
} from "@/lib/birthday";
import { occasionLabel, type OrderOccasion, type OrderOccasionInput, type OrderOccasionKind } from "./api";

export type OccasionDraft = {
  kind: OrderOccasionKind | "";
  date: string;
  dateError: string | null;
  person: "self" | "other" | "";
  personName: string;
  note: string;
};

export const EMPTY_DRAFT: OccasionDraft = { kind: "", date: "", dateError: null, person: "", personName: "", note: "" };

export function draftOf(o: OrderOccasion | null | undefined): OccasionDraft {
  if (!o) return EMPTY_DRAFT;
  return {
    kind: o.kind,
    date: o.date ?? "",
    dateError: null,
    person: o.person ?? "",
    personName: o.personName ?? "",
    note: o.note ?? "",
  };
}

/** Bản nháp điền sẵn từ chữ "sinh nhật 12/11" thấy trong ghi chú (9a-5). */
export function draftFromMention(text: string | null | undefined): OccasionDraft | null {
  const m = findBirthdayMention(text);
  if (!m) return null;
  const y = m.year ?? BIRTHDAY_NO_YEAR;
  const iso = `${y}-${String(m.month).padStart(2, "0")}-${String(m.day).padStart(2, "0")}`;
  return { ...EMPTY_DRAFT, kind: "birthday", date: iso };
}

/** Thân gửi máy chủ, hoặc lý do nút Lưu còn khoá. Rỗng kind = không ghi dịp. */
export function draftToInput(d: OccasionDraft): { input: OrderOccasionInput } | { error: string } {
  if (!d.kind) return { input: { kind: "" } };
  if (d.dateError) return { error: d.dateError };
  if (d.kind === "birthday") {
    if (!d.date) return { error: "Nhập ngày và tháng sinh nhật" };
    if (!d.person) return { error: "Chọn sinh nhật của ai" };
  }
  const input: OrderOccasionInput = { kind: d.kind };
  if (d.date) input.date = d.date;
  if (d.kind === "birthday" && d.person) {
    input.person = d.person;
    if (d.person === "other" && d.personName.trim()) input.personName = d.personName.trim();
  } else if (d.kind !== "birthday" && d.personName.trim()) {
    input.personName = d.personName.trim();
  }
  if (d.note.trim()) input.note = d.note.trim();
  return { input };
}

/** "🎂 Sinh nhật 12/11 · bé Na" — một dòng tóm tắt dịp đã lưu. */
export function occasionSummary(o: OrderOccasion): string {
  const { label, icon } = occasionLabel(o.kind);
  const day = formatBirthday(o.date);
  const who = o.kind === "birthday" ? (o.person === "self" ? "người đặt" : o.personName || "người khác") : o.personName;
  return [`${icon} ${label}${day ? ` ${day}` : ""}`, who].filter(Boolean).join(" · ");
}

/**
 * Nhãn ưu tiên hậu kỳ (8a): dịp còn ≤ 7 ngày ⇒ "🎂 Sinh nhật 30/10 — cần ảnh
 * trước ngày" ("hôm nay" khi đúng ngày). Không ngày / còn xa ⇒ null.
 */
export function occasionSoon(o: OrderOccasion | null | undefined, today = new Date()): { text: string; days: number } | null {
  if (!o?.date) return null;
  const days = daysUntilAnnual(o.date, today);
  if (days == null || days > OCCASION_SOON_DAYS) return null;
  const { label, icon } = occasionLabel(o.kind);
  const day = formatBirthday(o.date).slice(0, 5);
  const when = days === 0 ? "hôm nay" : days === 1 ? "ngày mai" : `còn ${days} ngày`;
  return { text: `${icon} ${label} ${day} (${when}) — cần ảnh trước ngày`, days };
}
